import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { isFakeMouse } from '@hooks/touch/touchRules';
import classNames from 'classnames';
import PortalContainer from './PortalContainer';
import { UXPContext } from '@core/context/Context';
/**
 * This component wraps another component and shows a tooltip for the component it is wrapping, whenever the user moves the mouse over it.
 * @deprecated Use `Dropdown` instead (it opens as a bottom sheet on phones).
 * @export
 *
 *
 * @example
 * ```
 *  Dropdown button basic example
 *
 *  <DropDownButton
 *      content={() => <div>This is dropdown content</div>}
 *  >
 *      <button className="btn showcase" >Click to Show the dropdown</button>
 *  </DropDownButton>
 * ```
 *
 *
 * @example
 * ```
 *  Dropdown button example with options
 *
 *  <DropDownButton
 *      content={() => <div>This is dropdown content</div>}
 *      position="left"
 *      showOnHover
 *  >
 *      <button className="btn showcase" >Click to Show the dropdown</button>
 *  </DropDownButton>
 * ```
 *
 *
 * @example
 * ```
 *  Dropdown button example with forceClose
 *
 *  // Be carefull when handeling the state. `closePopup` state must reset to default (false) when the popup closes
 *  //state
 *  let [closePopup, setClosePopup] = React.useState(false)
 *
 *  <DropDownButton
 *      content={() => <>
 *                  <h1>Click to close</h1>
 *                  <Button title="Click" onClick={() => { setClosePopup(true) }} />
 *              </>}
 *      onOpen={() => { }}
 *      onClose={() => { setClosePopup(false) }}
 *      forceClose={closePopup}
 *  >
 *      <button className="btn showcase" >Click to Show the dropdown</button>
 *  </DropDownButton>
 *
 * ```
 *
 * @category Buttons
 */
const DropDownButton = (props) => {
    const context = React.useContext(UXPContext);
    const { content, showOnHover = false, keepShowingOnHover, className, onOpen, onClose, forceClose, autoPosition } = props;
    const gap = 10;
    const position = (context.direction == 'rtl')
        ? (props.position || 'bottom right')
            .replace('right', 'temp')
            .replace('left', 'right')
            .replace('temp', 'left')
        : props.position || 'bottom right';
    // state 
    const [show, setShow] = React.useState(false);
    const [coords, setCoords] = React.useState({});
    const [autoPositionClass, setAutoPositionClass] = React.useState('');
    // ref 
    const buttonRef = React.useRef(null);
    const dropBtnContentRef = React.useRef(null);
    const isMouseHovering = React.useRef(false);
    React.useEffect(() => {
        if (show) {
            if (onOpen) {
                onOpen();
            }
        }
        else {
            isMouseHovering.current = false;
            if (onClose) {
                onClose();
            }
        }
    }, [show]);
    React.useEffect(() => {
        if (forceClose)
            setShow(false);
    }, [forceClose]);
    React.useEffect(() => {
    }, [coords]);
    // Touch: a tap opens and closes it (the hover a tap fakes is ignored)
    const onMouseOver = (e) => {
        if (isFakeMouse())
            return;
        if (showOnHover || keepShowingOnHover) {
            if (!isMouseHovering.current) {
                isMouseHovering.current = true;
                toggleTooltip();
            }
        }
    };
    const onMouseOut = (e) => {
        if (isFakeMouse())
            return;
        if (showOnHover) {
            isMouseHovering.current = false;
            toggleTooltip();
        }
    };
    const onClick = (e) => {
        if (!showOnHover || isFakeMouse()) {
            toggleTooltip();
        }
    };
    // toggle tooltip
    const toggleTooltip = () => {
        const buttonDetails = buttonRef.current.getBoundingClientRect();
        const { innerWidth, innerHeight } = window;
        let _coords = { opacity: 0 };
        if (buttonDetails) {
            const { top, bottom, left, right, x, y, width, height } = buttonDetails;
            if (autoPosition) {
                // check which side has more space 
                const vAlign = top > (innerHeight - bottom) ? 'top' : 'bottom';
                const hAlign = left > (innerWidth - right) ? 'left' : 'right';
                if (vAlign == 'top') {
                    _coords.bottom = innerHeight - top + gap;
                    _coords.maxHeight = top - (2 * gap);
                }
                else {
                    _coords.top = bottom + gap;
                    _coords.maxHeight = (innerHeight - bottom) - (2 * gap);
                }
                if (hAlign == 'left') {
                    _coords.right = innerWidth - left + gap;
                    _coords.maxWidth = left - (2 * gap);
                }
                else {
                    _coords.left = right + gap;
                    _coords.maxWidth = (innerWidth - right) - (2 * gap);
                }
            }
            else {
                switch (position) {
                    case "bottom right":
                    case "right":
                        _coords = {
                            right: window.innerWidth - buttonDetails.right,
                            top: buttonDetails.y + buttonDetails.height + window.scrollY + 25
                        };
                        break;
                    case "bottom left":
                    case "left":
                        _coords = {
                            left: buttonDetails.left,
                            top: buttonDetails.y + buttonDetails.height + window.scrollY + 25
                        };
                        break;
                    case "bottom center":
                        _coords = {
                            left: buttonDetails.x + buttonDetails.width / 2,
                            top: buttonDetails.y + buttonDetails.height + window.scrollY + 25
                        };
                        break;
                    case "top right":
                        _coords = {
                            right: window.innerWidth - buttonDetails.right,
                            top: buttonDetails.y + window.scrollY - 25
                        };
                        break;
                    case "top left":
                        _coords = {
                            left: buttonDetails.left,
                            top: buttonDetails.y + window.scrollY - 25
                        };
                        break;
                    case "top center":
                        _coords = {
                            left: buttonDetails.x + buttonDetails.width / 2,
                            top: buttonDetails.y + window.scrollY - 25
                        };
                        break;
                    case "left center":
                        _coords = {
                            left: buttonDetails.x - 25,
                            top: buttonDetails.y + window.scrollY + buttonDetails.height / 2
                        };
                        break;
                    case "right center":
                        _coords = {
                            left: buttonDetails.x + buttonDetails.width + 25,
                            top: buttonDetails.y + window.scrollY + buttonDetails.height / 2
                        };
                        break;
                }
            }
        }
        setCoords(_coords);
        setShow(!show);
        setTimeout(() => {
            updatePosition(_coords);
        }, 100);
    };
    function updatePosition(_coords) {
        var _a;
        // check if the drop panel is outside the screen
        const { innerWidth, innerHeight } = window;
        const dropPanelDetails = (_a = dropBtnContentRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        const buttonDetails = buttonRef.current.getBoundingClientRect();
        if (dropPanelDetails && buttonDetails) {
            const { top, left, right, bottom, width, height, x, y } = dropPanelDetails;
            const { top: bTop, left: bLeft, right: bRight, bottom: bBottom, width: bWidth, height: bHeight, x: bX, y: bY } = buttonDetails;
            if (autoPosition) {
                const isAlignedToBottom = top > bBottom;
                const isAlignedToRight = left > bRight;
                const canAlignToTop = (top > bBottom && height <= (bTop - (2 * gap)));
                const canAlignToMiddleVertically = (top > bBottom && height / 2 <= (bTop - (2 * gap)));
                const canAlignToMiddleHorizontally = ((width / 2 <= (bLeft + bWidth / 2)) && (width / 2 <= (innerWidth - bRight + (bWidth / 2))));
                // allign to top
                if (canAlignToTop) {
                    _coords.top = 'unset';
                    _coords.bottom = innerHeight - bTop + gap;
                }
                // align to middle (vertically)
                else if (canAlignToMiddleVertically) {
                    _coords.top = 'unset';
                    _coords.bottom = innerHeight - (bTop + bHeight / 2) - (height / 2);
                }
                // align to middle horizontally 
                if ((!canAlignToMiddleVertically || canAlignToTop) && canAlignToMiddleHorizontally) {
                    _coords.left = bLeft + (bWidth / 2) - (width / 2);
                    _coords.right = 'unset';
                }
                const vClass = (!isAlignedToBottom || canAlignToTop) ? 'top' : (canAlignToMiddleVertically) ? 'middle' : 'bottom';
                const hClass = (canAlignToMiddleHorizontally && (!canAlignToMiddleVertically || canAlignToTop)) ? 'middle' : (isAlignedToRight) ? 'right' : 'left';
                setAutoPositionClass(`auto-${vClass} auto-${hClass}`);
            }
            else {
                switch (position) {
                    case "bottom right":
                    case "right":
                        if (x < 0) {
                            _coords.left = 10;
                            _coords.right = 'unset';
                        }
                        if ((y + height) > innerHeight) {
                            _coords.bottom = 10;
                            _coords.top = 'unset';
                        }
                        break;
                    case "bottom left":
                    case "left":
                        if ((x + width) > innerWidth) {
                            _coords.right = 10;
                            _coords.left = 'unset';
                        }
                        if ((y + height) > innerHeight) {
                            _coords.bottom = 10;
                            _coords.top = 'unset';
                        }
                        break;
                    case "bottom center":
                        if (x < 0) {
                            _coords.left = 10 + (width / 2);
                            _coords.right = 'unset';
                        }
                        else if ((x + width) > innerWidth) {
                            _coords.right = 10 - (width / 2);
                            _coords.left = 'unset';
                        }
                        if ((y + height) > innerHeight) {
                            _coords.bottom = 10;
                            _coords.top = 'unset';
                        }
                        break;
                    case "top right":
                        if (x < 0) {
                            _coords.left = 10;
                            _coords.right = 'unset';
                        }
                        if (y < 0) {
                            _coords.top = height + 10;
                            _coords.bottom = 'unset';
                        }
                        break;
                    case "top left":
                        if ((x + width) > innerWidth) {
                            _coords.right = 10;
                            _coords.left = 'unset';
                        }
                        if (y < 0) {
                            _coords.top = height + 10;
                            _coords.bottom = 'unset';
                        }
                        break;
                    case "top center":
                        if (x < 0) {
                            _coords.left = 10 + (width / 2);
                            _coords.right = 'unset';
                        }
                        else if ((x + width) > innerWidth) {
                            _coords.right = 10 - (width / 2);
                            _coords.left = 'unset';
                        }
                        if (y < 0) {
                            _coords.top = height + 10;
                            _coords.bottom = 'unset';
                        }
                        break;
                    case "left center":
                        if (x < 0) {
                            _coords.left = 10 + width;
                            _coords.right = 'unset';
                        }
                        if (y < 0) {
                            _coords.top = 10 + (height / 2);
                            _coords.bottom = 'unset';
                        }
                        else if ((y + height) > innerHeight) {
                            _coords.bottom = 10 - (height / 2);
                            _coords.top = 'unset';
                        }
                        break;
                    case "right center":
                        if ((x + width) > innerWidth) {
                            _coords.right = 10;
                            _coords.left = 'unset';
                        }
                        if (y < 0) {
                            _coords.top = 10 + (height / 2);
                            _coords.bottom = 'unset';
                        }
                        else if ((y + height) > innerHeight) {
                            _coords.bottom = 10 - (height / 2);
                            _coords.top = 'unset';
                        }
                        break;
                }
            }
        }
        setCoords(prev => (Object.assign(Object.assign(Object.assign({}, prev), _coords), { opacity: 1 })));
    }
    // render tooltip
    const renderTooltip = () => {
        let positionClass = autoPosition ? autoPositionClass : position;
        return (_jsx(PortalContainer, { hasBackdrop: !showOnHover, onClickBackdrop: () => setShow(false), disableScroll: props.disableScroll, children: _jsx("div", { className: classNames("uxpcore_dropdownbtn__content dropdown-btn-content", className, positionClass), style: Object.assign({}, coords), ref: dropBtnContentRef, children: content() }) }));
    };
    return (_jsxs(_Fragment, { children: [_jsx("span", { className: "uxpcore_dropdownbtn__container dropdown-btn", onMouseOver: onMouseOver, onMouseOut: onMouseOut, onClick: onClick, ref: buttonRef, children: props.children }), show && renderTooltip()] }));
};
export default DropDownButton;
